"use client";

import { IconCalendarTime, IconChevronDown } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useProjectText } from "@/features/project/useProjectText";
import { cn } from "@/lib/cn";
import { useTimeline } from "../useTimeline";
import { Stepper } from "./Stepper";

/**
 * The project's phases, along the bottom of the map.
 *
 * Collapses to a calendar button rather than disappearing: the map underneath is
 * what the steps change, so the control has to be able to get out of the way
 * without being lost.
 *
 * Renders nothing for a project with no timeline configured, which is most of
 * them.
 */
export function Timeline({ className }: { className?: string }) {
  const t = useTranslations("timeline");
  const projectText = useProjectText();
  const [isExpanded, setExpanded] = useState(true);
  const { steps, activeStepId, setStepId } = useTimeline();

  if (steps.length === 0) return null;

  // Only shown when the tenant authored one — there is no app-level default,
  // because what the phases are called is entirely a project's own business.
  const title = projectText("timeline.title");

  const labelled = steps.map((step) => ({
    id: step.id as string,
    label: projectText(`timeline.steps.${step.id}`, step.id),
  }));

  return (
    <div
      className={cn(
        "absolute bottom-0 left-1/2 -translate-x-1/2 group pointer-events-auto flex items-stretch justify-end",
        "transition-all duration-300 ease-in-out",
        isExpanded ? "mb-16 max-h-[400px]" : "mb-2 max-h-10",
        className,
      )}
    >
      <div
        className={cn(
          "flex flex-1 origin-right flex-col items-center gap-2 rounded-l-2xl",
          "bg-white bg-opacity-50 py-4 pl-4 shadow-md backdrop-blur",
          "transition-[transform,opacity] duration-300 ease-in-out",
          isExpanded
            ? "scale-x-100 opacity-90 group-hover:opacity-100"
            : "pointer-events-none scale-x-0 opacity-0",
        )}
        // Collapsed it is scaled to nothing, which leaves it clickable by
        // keyboard and readable by a screen reader while invisible.
        inert={!isExpanded}
      >
        {title && (
          <h3 className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold">
            {title}
          </h3>
        )}

        <Stepper
          steps={labelled}
          currentStepId={activeStepId}
          onStepChange={(id) => void setStepId(id)}
        />
      </div>

      <button
        type="button"
        onClick={() => setExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        title={isExpanded ? t("close") : t("open")}
        aria-label={isExpanded ? t("close") : t("open")}
        className={cn(
          "-ml-px flex items-center bg-white p-2 shadow-md backdrop-blur",
          "transition-[border-radius,background-opacity] hover:bg-opacity-60",
          "group-hover:opacity-100",
          isExpanded
            ? "rounded-r-2xl bg-opacity-50 opacity-90"
            : "rounded-3xl bg-opacity-80 delay-300 duration-0",
        )}
      >
        <IconCalendarTime
          aria-hidden
          className={cn(
            "text-neutral-700 transition-all",
            isExpanded ? "w-0 opacity-0" : "w-8",
          )}
        />
        <IconChevronDown
          size={16}
          aria-hidden
          className={cn(!isExpanded && "rotate-180")}
        />
      </button>
    </div>
  );
}
